import {
  getSignedUrlForGet,
  IDocumentAssemblerGeneration,
  renderDocumentAssemblerGenerationPreview,
} from '@commenda/api';
import { Alert, Button, Icons } from '@commenda/kakashi';
import { downloadFileFromLink } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';

interface DocumentAssemblerGenerationGenerateCardProps {
  generation: IDocumentAssemblerGeneration;
}

const describePending = (count: number): string =>
  count === 1
    ? 'One placeholder has no value, so the document will carry one marker.'
    : `${count} placeholders have no value, so the document will carry ${count} markers.`;

export const DocumentAssemblerGenerationGenerateCard = ({
  generation,
}: DocumentAssemblerGenerationGenerateCardProps) => {
  const { setNotification } = useNotification();
  const [isGenerating, setIsGenerating] = useState(false);

  const pendingCount =
    generation.unresolvedPlaceholdersAndTrees.placeholders.length;
  const undecidedCount =
    generation.unresolvedPlaceholdersAndTrees.conditionalTrees.length;

  const onDocumentGenerate = async () => {
    setIsGenerating(true);

    try {
      const { preview } = await renderDocumentAssemblerGenerationPreview(
        generation.id
      );
      const signedUrlResult = await getSignedUrlForGet(preview.generatedFileId);

      if (!signedUrlResult?.signedUrl) {
        throw new Error(
          'The document was generated but could not be downloaded. Try again in a moment.'
        );
      }

      downloadFileFromLink(signedUrlResult.signedUrl, preview.fileName);
      setNotification({
        show: true,
        valid: true,
        title: 'Document generated',
        message:
          preview.pendingPlaceholders.length === 0
            ? 'Every placeholder had a value, so nothing is marked pending.'
            : describePending(preview.pendingPlaceholders.length),
      });
    } catch (error: unknown) {
      setNotification({
        show: true,
        valid: false,
        title: 'The document could not be generated',
        message:
          error instanceof Error ? error.message : 'Try again in a moment.',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
      <div className="flex flex-col gap-xs">
        <h2 className="txt-medium-medium text-text-primary">
          Generate a document from this generation
        </h2>
        <p className="txt-extra-small-regular text-text-secondary">
          Puts every value the run did write into the template and leaves each
          placeholder it did not as{' '}
          <span className="font-mono">PENDING:&#123;&#123; … &#125;&#125;</span>{' '}
          on the page, highlighted yellow, so the gaps can be filled in by hand.
          Nothing about this record changes, and the file is not the document
          the run was asked for.
        </p>
      </div>

      {undecidedCount > 0 ? (
        <Alert
          variant="warning"
          title="Some conditional blocks were never decided"
          description={`${undecidedCount} block(s) have no decision. Each one reaches the page with every branch still in it, one alternative after another, so nothing goes missing — but the choice between them is still to be made by hand.`}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-md">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icons.Download01 className="size-4" />}
          isLoading={isGenerating}
          onClick={onDocumentGenerate}
        >
          Generate and download
        </Button>
        <span className="txt-extra-small-regular text-text-tertiary">
          {pendingCount === 0
            ? 'Every placeholder has a value.'
            : describePending(pendingCount)}
        </span>
      </div>
    </div>
  );
};
